import * as yup from 'yup'
import { TestContext } from 'yup'
import { getFileExtension } from '@/utils'
import translation from 'i18next'
import { SOUND_RECORD_DESCRIPTION_LENGTH } from '@/constants'

export const mediaSchema = yup.object({
  chosenToUpload: yup.array().of(
    yup.object().shape({
      file: yup.mixed(),
      link: yup.string(),
      description: yup.string().optional(),
    })
  ),
  uploadedToApi: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      link: yup.string(),
    })
  ),
  stale: yup.array().of(yup.number()),
})

type Media = yup.InferType<typeof mediaSchema>

type ValidateMediaProps = {
  value: Media | undefined
  minItems?: number
  maxItems?: number
  accept?: string
  context?: TestContext
  description?: boolean
}

export const validateMedia = ({
  value,
  minItems = 1,
  maxItems = undefined,
  accept,
  context,
  description,
}: ValidateMediaProps) => {
  const { chosenToUpload = [], uploadedToApi = [], stale = [] } = value || {}

  const totalItems = chosenToUpload.length + uploadedToApi.length

  const allStale =
    uploadedToApi.length > 0 && uploadedToApi.every((m) => stale.includes(m.id))

  if (
    (maxItems && totalItems > maxItems) ||
    (minItems && totalItems < minItems) ||
    (minItems && allStale && chosenToUpload.length === 0)
  ) {
    return context && maxItems
      ? context.createError({
          message: translation.t('errors:validation.wrongNumberOfMediaFiles', {
            maxNumber: maxItems,
          }),
        })
      : false
  }

  if (accept && chosenToUpload.length > 0) {
    const acceptList = normalizeAccept(accept)
    const allValid = chosenToUpload.every(({ file }) =>
      matchesAccept(file, acceptList)
    )

    if (!allValid && context) {
      return context
        ? context.createError({
            message: translation.t(
              'errors:validation.wrongFormatOfMediaFiles',
              {
                format: accept,
              }
            ),
          })
        : false
    }
  }

  if (
    description &&
    chosenToUpload.length > 0 &&
    chosenToUpload.some(
      ({ description }) =>
        !description || description.length < SOUND_RECORD_DESCRIPTION_LENGTH
    )
  ) {
    return context
      ? context.createError({
          message: translation.t(
            'errors:validation.mediaFileDescriptionIsRequired'
          ),
        })
      : false
  }

  return true
}

const normalizeAccept = (accept: string): string[] =>
  accept
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean)

const matchesAccept = (file: File, acceptList: string[]): boolean => {
  const fileExt = getFileExtension(file.name)
  const fileType = file.type.toLowerCase()

  return acceptList.some((acceptItem) => {
    if (acceptItem.startsWith('.')) return fileExt === acceptItem
    if (acceptItem.endsWith('/*')) {
      const [typeMain] = acceptItem.split('/')

      return fileType.startsWith(typeMain + '/')
    }

    return fileType === acceptItem
  })
}
